/**
 * Pure helpers for the "Export fac-build-msup org" feature.
 *
 * Filters/merges user-placed map objects to produce:
 * - Facilities (kind === 'facility') excluding Assmat icons (those are merged
 *   into nearby Material entries) and excluding all MSup-family icons
 *   (handled in the MSup section instead).
 * - Build (kind === 'build') — typically `BUILD_BASE`.
 * - MSup: Maintenance Tunnels merged with their nearest MSup variant
 *   (5K/10K/30K), plus standalone tunnels/variants/`FACILITY_MSUP`.
 *
 * The merge logic mirrors `AssetListPanel.tsx`.
 */

import type { MapArrow, MapObject } from '@warplan/shared';
import { findRegionKey } from './foxhole-maps.js';

// ---------------------------------------------------------------------------
// Constants (kept in sync with AssetListPanel)
// ---------------------------------------------------------------------------

const TUNNEL_ID = 'LOGISTIC_MAINTENANCETUNNEL';
const MSUP_VARIANT_IDS = new Set(['LOGISTIC_MSUP_5K', 'LOGISTIC_MSUP_10K', 'LOGISTIC_MSUP_30K']);
const FACILITY_MSUP_ID = 'FACILITY_MSUP';

export const MSUP_VARIANT_SHORT: Record<string, string> = {
  LOGISTIC_MSUP_5K: '5K',
  LOGISTIC_MSUP_10K: '10K',
  LOGISTIC_MSUP_30K: '30K',
};

const MATERIAL_ID = 'FACILITY_MATERIAL';
const ASSMAT_IDS = new Set([
  'FACILITY_ASSMAT_1',
  'FACILITY_ASSMAT_2',
  'FACILITY_ASSMAT_3',
  'FACILITY_ASSMAT_4',
  'FACILITY_ASSMAT_5',
]);
export const ASSMAT_SHORT: Record<string, string> = {
  FACILITY_ASSMAT_1: 'AM1',
  FACILITY_ASSMAT_2: 'AM2',
  FACILITY_ASSMAT_3: 'AM3',
  FACILITY_ASSMAT_4: 'AM4',
  FACILITY_ASSMAT_5: 'AM5',
};

/** Icon ids belonging to the MSup family (tunnel + variants + FACILITY_MSUP). */
const MSUP_FAMILY_IDS = new Set<string>([
  TUNNEL_ID,
  FACILITY_MSUP_ID,
  ...MSUP_VARIANT_IDS,
]);

const MERGE_RADIUS_SQ = 30 * 30;
const FACILITY_MERGE_RADIUS_SQ = 30 * 30;

// ---------------------------------------------------------------------------
// Display types
// ---------------------------------------------------------------------------

export type FacilityItem =
  | { kind: 'merged'; material: MapObject; assmats: MapObject[] }
  | { kind: 'standalone'; obj: MapObject };

export type MsupItem =
  | { kind: 'merged'; tunnel: MapObject; msup: MapObject }
  | { kind: 'standalone'; obj: MapObject };

// ---------------------------------------------------------------------------
// Filtering
// ---------------------------------------------------------------------------

function inActiveRegion(obj: MapObject, disabled: ReadonlySet<string>): boolean {
  if (disabled.size === 0) return true;
  const k = findRegionKey(obj.position.x, obj.position.y);
  return k === null || !disabled.has(k);
}

/** Logical scope of the export — controls which markers/sections appear. */
export type FacBuildMsupScope = 'fac-build' | 'msup' | 'all';

/** True if `obj` belongs to the fac/build/msup export scope. */
export function isFacBuildMsupExportable(
  obj: MapObject,
  scope: FacBuildMsupScope = 'all',
): boolean {
  const isFacility = obj.kind === 'facility' && !MSUP_FAMILY_IDS.has(obj.metadata.iconId);
  const isBuild = obj.kind === 'build';
  const isMsup =
    (obj.kind === 'logistic' || obj.kind === 'facility') &&
    MSUP_FAMILY_IDS.has(obj.metadata.iconId);
  if (scope === 'fac-build') return isFacility || isBuild;
  if (scope === 'msup') return isMsup;
  return isFacility || isBuild || isMsup;
}

/**
 * True if `arrow` belongs to the fac-build-msup export scope. Because arrows
 * do not carry an `iconId` (only a category), the mapping is coarse:
 *  - `fac-build` → `facility` or `build` arrows
 *  - `msup`      → `facility` or `logistic` arrows (covers tunnel/MSup intent)
 *  - `all`       → union of the above
 */
export function isFacBuildMsupExportableArrow(
  arrow: MapArrow,
  scope: FacBuildMsupScope = 'all',
): boolean {
  if (scope === 'fac-build') return arrow.kind === 'facility' || arrow.kind === 'build';
  if (scope === 'msup') return arrow.kind === 'facility' || arrow.kind === 'logistic';
  return (
    arrow.kind === 'facility' ||
    arrow.kind === 'build' ||
    arrow.kind === 'logistic'
  );
}

const EMPTY_IDS: ReadonlySet<string> = new Set();

/**
 * Returns the markers that should appear on the export map for the given scope,
 * restricted to active regions and excluding any object whose id is in
 * `excludedIds` (manual user selection in the filter panel).
 */
export function filterFacBuildMsupMarkers(
  objects: ReadonlyArray<MapObject>,
  disabled: ReadonlySet<string>,
  scope: FacBuildMsupScope = 'all',
  excludedIds: ReadonlySet<string> = EMPTY_IDS,
): MapObject[] {
  return objects.filter(
    (o) =>
      isFacBuildMsupExportable(o, scope) &&
      inActiveRegion(o, disabled) &&
      !excludedIds.has(o.id),
  );
}

// ---------------------------------------------------------------------------
// Merging helpers
// ---------------------------------------------------------------------------

function distSq(ax: number, ay: number, bx: number, by: number): number {
  return (ax - bx) ** 2 + (ay - by) ** 2;
}

/**
 * Pair each Maintenance Tunnel with the closest MSup variant within
 * MERGE_RADIUS_SQ. Unmatched tunnels and variants are kept standalone.
 * `FACILITY_MSUP` entries are always standalone.
 */
export function buildMsupItems(objects: ReadonlyArray<MapObject>): MsupItem[] {
  const msupFamily = objects.filter(
    (o) =>
      (o.kind === 'logistic' || o.kind === 'facility') &&
      MSUP_FAMILY_IDS.has(o.metadata.iconId),
  );

  const tunnels = msupFamily.filter((o) => o.metadata.iconId === TUNNEL_ID);
  const variants = msupFamily.filter((o) => MSUP_VARIANT_IDS.has(o.metadata.iconId));
  const facilityMsup = msupFamily.filter((o) => o.metadata.iconId === FACILITY_MSUP_ID);

  const usedVariantIds = new Set<string>();
  const result: MsupItem[] = [];

  for (const tunnel of tunnels) {
    let best: MapObject | null = null;
    let bestDist = MERGE_RADIUS_SQ;
    for (const v of variants) {
      if (usedVariantIds.has(v.id)) continue;
      const d = distSq(tunnel.position.x, tunnel.position.y, v.position.x, v.position.y);
      if (d <= bestDist) {
        bestDist = d;
        best = v;
      }
    }
    if (best) {
      usedVariantIds.add(best.id);
      result.push({ kind: 'merged', tunnel, msup: best });
    } else {
      result.push({ kind: 'standalone', obj: tunnel });
    }
  }

  for (const v of variants) {
    if (!usedVariantIds.has(v.id)) result.push({ kind: 'standalone', obj: v });
  }
  for (const fm of facilityMsup) {
    result.push({ kind: 'standalone', obj: fm });
  }
  return result;
}

/**
 * Pair each Material with ALL Assmat icons within FACILITY_MERGE_RADIUS_SQ.
 * Unmatched Materials and standalone facility (non-Material/non-Assmat) entries
 * are kept standalone. MSup-family items are excluded entirely (handled by
 * `buildMsupItems`).
 */
export function buildFacilityItems(objects: ReadonlyArray<MapObject>): FacilityItem[] {
  const facilities = objects.filter(
    (o) => o.kind === 'facility' && !MSUP_FAMILY_IDS.has(o.metadata.iconId),
  );
  const materials = facilities.filter((o) => o.metadata.iconId === MATERIAL_ID);
  const assmats = facilities.filter((o) => ASSMAT_IDS.has(o.metadata.iconId));
  const others = facilities.filter(
    (o) => o.metadata.iconId !== MATERIAL_ID && !ASSMAT_IDS.has(o.metadata.iconId),
  );

  const usedAssmatIds = new Set<string>();
  const result: FacilityItem[] = [];

  for (const mat of materials) {
    const nearby = assmats.filter(
      (a) =>
        !usedAssmatIds.has(a.id) &&
        distSq(mat.position.x, mat.position.y, a.position.x, a.position.y) <=
          FACILITY_MERGE_RADIUS_SQ,
    );
    if (nearby.length > 0) {
      nearby.forEach((a) => usedAssmatIds.add(a.id));
      const seenTypes = new Set<string>();
      const unique = nearby.filter((a) => {
        if (seenTypes.has(a.metadata.iconId)) return false;
        seenTypes.add(a.metadata.iconId);
        return true;
      });
      const sortedAssmats = [...unique].sort((a, b) =>
        (ASSMAT_SHORT[a.metadata.iconId] ?? '').localeCompare(
          ASSMAT_SHORT[b.metadata.iconId] ?? '',
        ),
      );
      result.push({ kind: 'merged', material: mat, assmats: sortedAssmats });
    } else {
      result.push({ kind: 'standalone', obj: mat });
    }
  }

  for (const a of assmats) {
    if (!usedAssmatIds.has(a.id)) result.push({ kind: 'standalone', obj: a });
  }
  for (const o of others) {
    result.push({ kind: 'standalone', obj: o });
  }

  return result;
}

/** Build the ordered list of builds in the export scope. */
export function selectBuilds(objects: ReadonlyArray<MapObject>): MapObject[] {
  return objects.filter((o) => o.kind === 'build');
}

/** File name for the exported PNG. */
export function buildFacBuildMsupFileName(
  scope: FacBuildMsupScope = 'all',
  now: Date = new Date(),
): string {
  const pad = (n: number): string => n.toString().padStart(2, '0');
  const stamp =
    `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}` +
    `-${pad(now.getHours())}${pad(now.getMinutes())}`;
  const prefix =
    scope === 'fac-build' ? 'fac-build' : scope === 'msup' ? 'msup' : 'fac-build-msup';
  return `${prefix}-${stamp}.png`;
}
